import React, { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Bell, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { postsApi } from '../../services/postsApi';

// Builds a human-readable line for a community notification.
export const notificationText = (n, language = 'en') => {
  const es = language === 'es';
  const who = n.actor_name || 'Someone';
  switch (n.type) {
    case 'friend_accepted': return es ? `${who} aceptó tu solicitud de amistad` : `${who} accepted your friend request`;
    case 'new_follower': return es ? `${who} empezó a seguirte` : `${who} started following you`;
    case 'activity_liked': return es ? `A ${who} le gustó tu publicación` : `${who} liked your post`;
    case 'post_liked': return es ? `A ${who} le gustó tu publicación` : `${who} liked your post`;
    case 'post_commented': return es ? `${who} comentó tu publicación` : `${who} commented on your post`;
    case 'activity_commented': return es ? `${who} comentó tu publicación` : `${who} commented on your post`;
    case 'post_reaction': return es ? `${who} reaccionó a tu testimonio` : `${who} reacted to your testimony`;
    case 'story_reaction': return es ? `${who} reaccionó a tu historia` : `${who} reacted to your story`;
    case 'story_reply': return es ? `${who} respondió a tu historia` : `${who} replied to your story`;
    case 'mention': return es ? `${who} te mencionó` : `${who} mentioned you`;
    case 'followed_posted': return es ? `${who} compartió una nueva publicación` : `${who} shared a new post`;
    case 'post_milestone': return es ? `🎉 ¡Tu publicación llegó a ${n.likes_count} me gusta!` : `🎉 Your post reached ${n.likes_count} likes!`;
    case 'new_message': return es ? `${who} te envió un mensaje` : `${who} sent you a message`;
    case 'group_message': return es ? `Nuevo mensaje de ${who}` : `New group message from ${who}`;
    case 'group_mention': return es ? `${who} te mencionó en un grupo` : `${who} mentioned you in a group`;
    case 'group_added': return es ? `${who} te añadió a un grupo` : `${who} added you to a group`;
    case 'prayer_prayed': return es ? `${who} está orando por tu petición 🙏` : `${who} is praying for your request 🙏`;
    case 'prayer_answered': return es ? `¡Una oración fue respondida! 🙌` : `A prayer was answered! 🙌`;
    case 'partner_invite': return es ? `${who} te invitó a ser compañeros de oración 🤝` : `${who} invited you to be prayer partners 🤝`;
    case 'partner_accepted': return es ? `${who} aceptó tu invitación de oración 🤝` : `${who} accepted your prayer partner invite 🤝`;
    case 'partner_nudge': return es ? `${who} acaba de orar — ¡tu turno! 🙏` : `${who} just prayed — your turn! 🙏`;
    case 'streak_reminder': return es
      ? `🔥 Mantén viva tu racha de lectura de ${n.streak_count} día${n.streak_count === 1 ? '' : 's'} — lee un capítulo hoy`
      : `🔥 Keep your ${n.streak_count}-day reading streak alive — read a chapter today`;
    case 'admin_warning': return (es ? 'Aviso de moderación: ' : 'Moderator notice: ') + (n.message || '');
    default: return es ? 'Nueva notificación' : 'New notification';
  }
};

// Notification types whose actor has a profile worth opening.
const PROFILE_TYPES = new Set([
  'friend_accepted', 'new_follower', 'activity_liked', 'post_liked', 'post_commented',
  'activity_commented', 'post_reaction', 'story_reaction', 'story_reply', 'mention', 'followed_posted',
]);

// Inline "Follow back" for new followers you don't follow yet.
const FollowBackButton = ({ n, es }) => {
  const [done, setDone] = useState(!!n.you_follow_back);
  if (n.type !== 'new_follower' || !n.actor_id) return null;
  const go = async (e) => {
    e.stopPropagation();
    setDone(true);
    try { await postsApi.follow(n.actor_id); } catch { setDone(false); }
  };
  return (
    <span role="button" tabIndex={0} onClick={done ? undefined : go}
      className={`shrink-0 self-center rounded-full px-3 py-1 text-xs font-semibold ${done ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
      data-testid={`notification-follow-back-${n.id}`}>
      {done ? (es ? 'Siguiendo' : 'Following') : (es ? 'Seguir también' : 'Follow back')}
    </span>
  );
};

const NotificationBell = ({ notifications = [], unreadCount = 0, onOpenMarkRead, onOpenProfile, language = 'en' }) => {
  const es = language === 'es';
  const [open, setOpen] = useState(false);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v && unreadCount > 0) onOpenMarkRead?.();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative hover:bg-accent"
          title={es ? 'Notificaciones' : 'Notifications'}
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-semibold text-sm">{es ? 'Notificaciones' : 'Notifications'}</p>
          {notifications.some((n) => !n.read) && (
            <button
              onClick={() => onOpenMarkRead?.()}
              className="text-xs text-primary hover:underline flex items-center gap-1"
              data-testid="notification-mark-read"
            >
              <Check className="w-3 h-3" /> {es ? 'Marcar leído' : 'Mark read'}
            </button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {notifications.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-10" data-testid="notification-empty">
              {es ? 'Sin notificaciones aún' : 'No notifications yet'}
            </p>
          ) : (
            <div>
              {notifications.map((n) => {
                const clickable = PROFILE_TYPES.has(n.type) && n.actor_id;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => { if (clickable) { onOpenProfile?.(n.actor_id); setOpen(false); } }}
                    className={`flex items-start gap-3 w-full px-4 py-3 text-left border-b border-border/60 transition-colors hover:bg-accent ${n.read ? '' : 'bg-primary/5'}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <Avatar className="w-8 h-8 shrink-0">
                      {n.actor_picture ? <AvatarImage src={n.actor_picture} /> : null}
                      <AvatarFallback className="text-[10px] bg-primary/15">
                        {(n.actor_name || '?').slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm leading-snug">{notificationText(n, language)}</p>
                      {n.created_at && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {(() => { try { return formatDistanceToNow(new Date(n.created_at), { addSuffix: true }); } catch { return ''; } })()}
                        </p>
                      )}
                    </div>
                    <FollowBackButton n={n} es={es} />
                    {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
